<template>
  <div class="md-mask" v-if="show">
    <div class="md-container">
      <div class="md-dialog-header">
        <div class="md-header-content">
          <div class="icon-pill">
            <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.8" stroke-linecap="round" stroke-linejoin="round" style="width:16px;height:16px;"><rect x="3" y="11" width="18" height="11" rx="2" ry="2"/><path d="M7 11V7a5 5 0 0 1 10 0v4"/></svg>
          </div>
          <div>
            <h3 class="md-title">Change Password</h3>
            <p class="md-subtitle">Runner: {{ runnerName }} (ID: {{ runnerId }})</p>
          </div>
        </div>
        <button class="md-close-btn" @click="close">×</button>
      </div>
      
      <div class="md-dialog-body">
        <div class="form-group" style="display:flex; flex-direction:column; gap:8px;">
          <label for="new-password" class="md-label" style="font-size:12px; margin:0;">New Password</label>
          <input 
            type="password" 
            id="new-password" 
            v-model="password" 
            class="md-input"
            placeholder="Enter new password"
            @keyup.enter="savePassword"
          >
        </div>
        
        <div v-if="error" class="md-error">
          {{ error }}
        </div>
      </div>
      
      <div class="md-dialog-footer">
        <button class="md-ghost-btn" @click="close">
          Cancel
        </button>
        <button class="md-primary-btn" @click="savePassword" :disabled="loading || !password || password.length < 4">
          {{ loading ? 'Saving...' : 'Save Password' }}
        </button>
      </div>
    </div>
  </div>
</template>

<script>
import runnersService from '@/services/runnersService'

export default {
  name: 'PasswordManagementModal',
  props: {
    show: {
      type: Boolean,
      default: false
    },
    runnerId: {
      type: [Number, String],
      required: true
    },
    runnerName: {
      type: String,
      default: ''
    }
  },
  data() {
    return {
      password: '',
      loading: false,
      error: ''
    }
  },
  watch: {
    show(val) {
      if (val) {
        this.password = ''
        this.error = ''
      }
    }
  },
  methods: {
    close() {
      this.$emit('close')
    },
    async savePassword() {
      if (!this.password || this.password.length < 4) {
        this.error = 'Password must be at least 4 characters long'
        return
      }
      
      this.loading = true
      this.error = ''
      
      try {
        await runnersService.updatePassword(this.runnerId, this.password)
        this.$emit('saved')
        this.close()
        // Ideally we would show a success toast here, but relying on parent to handle notification if needed
      } catch (err) {
        console.error('Failed to change password:', err)
        this.error = err.response && err.response.data && err.response.data.message 
          ? err.response.data.message 
          : 'Failed to update password. Please try again.'
      } finally {
        this.loading = false
      }
    }
  }
}
</script>

<style scoped>
.md-mask {
  position: fixed; z-index: 9998; top: 0; left: 0; width: 100%; height: 100%;
  background-color: rgba(0, 0, 0, 0.5); display: flex; justify-content: center; align-items: center;
}
.md-container {
  width: 480px; background-color: #fff; border-radius: 14px;
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.15); display: flex; flex-direction: column;
}
.md-dialog-header {
  display: flex; justify-content: space-between; align-items: center;
  padding: 1.25rem 1.5rem; border-bottom: 1px solid #f0f0f5;
}
.md-header-content { display: flex; align-items: center; gap: 12px; }
.icon-pill {
  width: 36px; height: 36px; border-radius: 10px; background: #f0effe; color: #5b4fcf;
  display: flex; justify-content: center; align-items: center;
}
.md-title { font-size: 16px; font-weight: 700; color: #1a1a2e; margin: 0; }
.md-subtitle { font-size: 12px; color: #8a8a9a; margin: 0; }
.md-close-btn {
  width: 30px; height: 30px; border: 1px solid #e8e8ed; background: transparent; cursor: pointer;
  border-radius: 8px; font-size: 20px; display: flex; justify-content: center; align-items: center; color: #aaa;
}
.md-close-btn:hover { color: #c0392b; border-color: #fdd; background: #fff5f5; }
.md-dialog-body { padding: 1.5rem 1.75rem; display: flex; flex-direction: column; gap: 1.1rem; }
.md-label { font-size: 13px; font-weight: 600; color: #1a1a2e; margin-bottom: 4px; }
.md-input {
  width: 100%; padding: 10px 12px; border: 1px solid #e8e8ed; border-radius: 8px; font-size: 13px; color: #1a1a2e; background-color: #fafafa; outline: none; transition: all 0.2s ease;
}
.md-input:focus { border-color: #5b4fcf; box-shadow: 0 0 0 3px rgba(91, 79, 207, 0.1); background-color: #fff; }
.md-error { background: #fff5f5; border: 1px solid #fdd; border-radius: 7px; padding: 7px 12px; font-size: 12px; color: #c0392b; margin-top: 5px; }
.md-dialog-footer {
  border-top: 1px solid #f0f0f5; padding: 1rem 1.75rem 1.5rem; display: flex; justify-content: flex-end; gap: 10px;
}
.md-primary-btn {
  background: #5b4fcf; color: #fff; border: none; height: 36px; padding: 0 18px; border-radius: 8px; font-size: 13px; font-weight: 600; cursor: pointer;
}
.md-primary-btn:hover { background: #4338ca; }
.md-primary-btn:disabled { opacity: 0.5; cursor: not-allowed; }
.md-ghost-btn {
  border: 1.5px solid #e8e8ed; background: #fff; color: #1a1a2e; height: 36px; padding: 0 16px; border-radius: 7px; font-size: 13px; font-weight: 600; cursor: pointer;
}
.md-ghost-btn:hover { border-color: #d0d0e0; }
</style>



// WEBPACK FOOTER //
// PasswordManagementModal.vue?7546d122